import { FC, useState } from "react"
import { css, Theme } from "@emotion/react"
import { DbUserPinModal } from "~/shared-components"
import { useIsUser, UserId, useAuth } from "@ncs/ncs-api"
import { Box, Button, EventTracker, Link, Paragraph, TrackingEvent, Icon } from "@ncs/web-legos"
import { useOrderTotals } from "~/contexts"
import { useAnalytics } from "~/hooks/useAnalytics"

export const ShopBarCartSummaryEcomm: FC = () => {
	const isDb = useIsUser(UserId.DrivenBrands)
	const { user, isPrivilegedDbUser } = useAuth()
	const { totalItemCount } = useOrderTotals(false)
	const [showDbPinModal, setShowDbPinModal] = useState(false)
	const { trackEvent } = useAnalytics()

	return (
		<>
			<div css={cartStatusContainerStyle}>
				{isDb ?
					isPrivilegedDbUser ?
						<Box display="inline" css={partOnStyle}>
							<Icon icon="unlock" color="text" />
							<span>Parts ON</span>
						</Box>
					:	<Button
							icon="lock"
							css={partPinStyle}
							onClick={() => setShowDbPinModal(true)}
						>
							<span>Parts PIN</span>
						</Button>

				: user ?
					<Box display="inline" css={quickOrderBoxCss}>
						<EventTracker event={TrackingEvent.QUICK_ORDER} trackEvent={trackEvent}>
							<Link to="/shop/quick-order" underline={false}>
								<Paragraph css={quickOrderCss}>Quick Order</Paragraph>
							</Link>
						</EventTracker>
					</Box>
				:	null}
				<EventTracker
					events={[TrackingEvent.CART_ICON_GO_TO_CART, TrackingEvent.GO_TO_CART]}
					trackEvent={trackEvent}
				>
					<Link
						to="/shop/cart-summary"
						aria-label={`Cart with ${totalItemCount} item${
							totalItemCount !== 1 ? "s" : ""
						}`}
					>
						<div css={shoppingCart}>
							<Icon icon="shopping-cart" css={cartIconStyle} aria-hidden="true" />
							{totalItemCount > 0 && (
								<div css={cartCountIndicatorStyle}>
									<span>{formatItemCount(totalItemCount)}</span>
								</div>
							)}
						</div>
					</Link>
				</EventTracker>
			</div>
			{showDbPinModal && <DbUserPinModal onClose={() => setShowDbPinModal(false)} />}
		</>
	)
}

const formatItemCount = (count: number): string => {
	return count > 99 ? "99+" : count.toString()
}

const cartIconStyle = (theme: Theme) => css`
	height: 1.5rem;
	width: 1.5rem;
	display: inline-block;
	padding-left: 6px;
	padding-top: 4px;

	path {
		fill: #00050a;
	}

	${theme.breakpoints.down("sm")} {
		path {
			fill: white;
		}
	}
`

const quickOrderBoxCss = (theme: Theme) => css`
	${theme.breakpoints.down("sm")} {
		vertical-align: super;
		font-weight: 700;
	}
	${theme.breakpoints.up("sm")} {
		height: 30px;
	}
`

const quickOrderCss = (theme: Theme) => css`
	font-size: 14px;
	font-style: normal;
	font-weight: 500;
	line-height: normal;
	vertical-align: top;
	height: 26px;
	margin-right: 10px;
	color: black;

	${theme.breakpoints.down("sm")} {
		color: white;
	}
`

const cartStatusContainerStyle = (theme: Theme) => css`
	margin-left: auto;
	display: flex;
	flex-direction: row;
	align-items: center;
	a {
		text-decoration: none;
	}
	${theme.breakpoints.down("md")} {
		& * {
			color: white;
		}
	}
	${theme.breakpoints.up("md")} {
		path {
			fill: black;
		}
	}

	span.aria-hidden {
		display: none;
	}
`
const shoppingCart = (theme: Theme) => css`
	display: flex;
	align-items: center;
	position: relative;
`
const cartCountIndicatorStyle = (theme: Theme) => css`
	display: flex;
	justify-content: center;
	align-items: center;
	position: absolute;
	top: -0.6rem;
	right: -1rem;
	background: #e02104;
	padding: 0.25rem;
	border-radius: 100%;
	padding: 0.7rem;
	width: 0.2rem;
	height: 0.6rem;
	color: black;

	span {
		color: white;
		font-size: 11px;
	}
`

const partPinStyle = (theme: Theme) => css`
	svg {
		margin-right: 2px;
		margin-bottom: 2px;
		width: 14px;
		height: 16px;
	}

	span {
		font-size: 14px;
		font-style: normal;
		font-weight: 500;
		line-height: normal;
	}

	${theme.breakpoints.down("sm")} {
		margin-right: 8px;
	}
`

const partOnStyle = (theme: Theme) => css`
	svg {
		margin-right: 8px;
		width: 18px;
		height: 16px;
		vertical-align: middle;
		margin-top: -4px;
	}

	span {
		color: #00050a;
		text-align: center;
		font-size: 14px;
		font-style: normal;
		font-weight: 500;
		line-height: normal;
	}

	${theme.breakpoints.down("sm")} {
		padding-bottom: 5px;

		span {
			color: white;
		}

		span:last-child {
			margin-right: 8px;
		}

		path {
			fill: white;
		}
	}
`
